import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Award, ChevronRight, Brain, FileText, Database, Package, MessageSquare, CheckCircle, Zap } from 'lucide-react';
import { adminRHCategories, adminRHLevels, type AdminRHCategory } from '../data/adminRHReferentiel';
import AdminRHSpiderChart from './AdminRHSpiderChart';

const iconMap: Record<string, any> = {
  FileText,
  Users,
  Database,
  Package,
  MessageSquare,
  CheckCircle,
  Zap,
};

interface AdminRHReferentielModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AdminRHReferentielModal({ isOpen, onClose }: AdminRHReferentielModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<AdminRHCategory | null>(null);

  const getCurrentLevel = (score: number) => {
    if (score >= 90) return adminRHLevels[2];
    if (score >= 75) return adminRHLevels[1];
    if (score >= 60) return adminRHLevels[0];
    return null;
  };

  const globalScore = Math.round(
    adminRHCategories.reduce((sum, cat) => sum + cat.currentScore, 0) / adminRHCategories.length
  );
  const currentLevel = getCurrentLevel(globalScore);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-40"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-xl shadow-2xl max-w-[98vw] w-full max-h-[98vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-6 rounded-t-xl z-10">
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full ring-4 ring-white/30 overflow-hidden shadow-lg bg-white/20 flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h2 className="text-3xl font-bold">Référentiel Admin/RH - Standards d&apos;Excellence</h2>
                    <span className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full text-xs font-semibold text-purple-100">
                      <Brain className="w-4 h-4" /> AI
                    </span>
                  </div>
                  <p className="text-white/90">Cadre de compétences BZ pour les rôles Administration & RH (Junior à Senior Prime)</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-white/95 text-sm font-medium">Leaders:</span>
                    <span className="text-white font-semibold text-sm">Mélissa Pelletier, Louis-Alexandre Hébert</span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="text-white hover:bg-white/10 rounded-full p-2 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mt-6 flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Award className="w-12 h-12 text-amber-200" />
                <div>
                  <div className="text-sm text-white/70">Score Global</div>
                  <div className="text-3xl font-bold">{globalScore}%</div>
                </div>
              </div>
              {currentLevel && (
                <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-white/70">Niveau Actuel</div>
                      <div className="text-xl font-bold" style={{ color: currentLevel.color }}>{currentLevel.level}</div>
                      <div className="text-xs text-white/60">{currentLevel.description}</div>
                    </div>
                    <div className="flex gap-2">
                      {adminRHLevels.map((level) => (
                        <div
                          key={level.level}
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: level.color,
                            opacity: globalScore >= level.minScore ? 1 : 0.3,
                          }}
                          title={`${level.level}: ${level.minScore}%+`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 mt-4 bg-white/10 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Niveaux de Progression Admin/RH</h3>
              <div className="grid grid-cols-3 gap-4">
                {adminRHLevels.map((level) => (
                  <div
                    key={level.level}
                    className={`p-3 rounded-lg border-2 ${globalScore >= level.minScore ? 'bg-white/20 border-white/40' : 'bg-white/5 border-white/20'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: level.color }} />
                      <h4 className="font-bold text-sm text-white">{level.level}</h4>
                    </div>
                    <div className="text-lg font-bold text-white/90">{level.minScore}%+</div>
                    <p className="text-xs text-white/70">{level.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 8 Catégories */}
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">8 Catégories de Compétences</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {adminRHCategories.map((category, index) => {
                const Icon = iconMap[category.icon] || FileText;
                const meetsMinimum = category.currentScore >= category.minScore;
                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setSelectedCategory(category)}
                    className="bg-white border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:border-teal-500 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-3 rounded-lg" style={{ backgroundColor: `${category.color}20` }}>
                        <Icon className="w-6 h-6" style={{ color: category.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-bold text-gray-800 group-hover:text-teal-600 transition-colors">{category.name}</h4>
                          <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 transition-colors" />
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Minimum: {category.minScore}%</span>
                            <span className="font-bold" style={{ color: meetsMinimum ? category.color : '#E74C3C' }}>
                              Score: {category.currentScore}%
                            </span>
                          </div>
                          <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${category.currentScore}%` }}
                              transition={{ duration: 0.8, delay: index * 0.05 }}
                              className="h-full rounded-full"
                              style={{ backgroundColor: category.color }}
                            />
                          </div>
                        </div>
                        <div className="mt-2 text-xs">
                          {meetsMinimum ? <span className="text-green-600 font-medium">✓ Atteint</span> : <span className="text-red-600 font-medium">⚠ {category.minScore - category.currentScore}% à gagner</span>}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Spider Chart */}
          <div className="p-6 bg-gray-50 border-t">
            <AdminRHSpiderChart />
          </div>

          {/* RACI note */}
          <div className="p-6 border-t bg-teal-50/50">
            <p className="text-sm text-gray-700">
              <strong>RACI officiel Administration & RH</strong> : disponible dans la section dédiée de cette page (carte « RACI officiel – Administration & RH »).
            </p>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl flex justify-between items-center border-t">
            <div className="text-sm text-gray-600">
              <strong>Note:</strong> Ce référentiel sert à évaluer et développer les compétences Admin/RH BZ (Junior Prime Admin → Senior Prime Admin).
            </div>
            <button onClick={onClose} className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition-colors">
              Fermer
            </button>
          </div>
        </div>
      </motion.div>

      {/* Modal Détail Catégorie */}
      <AnimatePresence>
        {selectedCategory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            onClick={() => setSelectedCategory(null)}
          >
            <div className="absolute inset-0 bg-black/60" />
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden flex flex-col"
            >
              <div className="p-6 text-white rounded-t-xl" style={{ backgroundColor: selectedCategory.color }}>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{selectedCategory.name}</h3>
                    <p className="text-white/90">{selectedCategory.description}</p>
                  </div>
                  <button onClick={() => setSelectedCategory(null)} className="text-white hover:bg-white/10 rounded-full p-2">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-8 flex-1 overflow-y-auto">
                <h4 className="text-lg font-bold text-gray-800 mb-4">Sous-Compétences ({selectedCategory.subCompetences.length})</h4>
                <div className="space-y-4">
                  {selectedCategory.subCompetences.map((sub) => (
                    <div
                      key={sub.id}
                      className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200 hover:border-teal-300 transition-all"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-bold text-gray-800">{sub.name}</h5>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Min: {sub.minRequired}%</div>
                          <div className="text-lg font-bold" style={{ color: sub.score >= sub.minRequired ? selectedCategory.color : '#E74C3C' }}>{sub.score}%</div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{sub.description}</p>
                      <div className="bg-white rounded-full h-2 overflow-hidden">
                        <div className="relative h-full">
                          <div className="absolute h-full bg-gray-300" style={{ width: `${sub.minRequired}%` }} />
                          <div className="absolute h-full rounded-full" style={{ width: `${sub.score}%`, backgroundColor: selectedCategory.color }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t p-4 flex justify-end">
                <button onClick={() => setSelectedCategory(null)} className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium">
                  Fermer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}
